import React, { useMemo } from "react";

// ---------- MachineTimelinePeek ----------
// Compact 30-day horizontal strip rendered inline inside an OCCUPIED row on
// the Planning Calculator's Freeze Capacity list. Shows amber cells for every
// day already booked by an active (Planned/Running) plan on this loom and
// gray cells for free days, so the planner can see — without leaving this
// screen — exactly WHEN the machine frees up and WHERE the next open slot is.
//
// A subtle emerald wedge marks the "Free from" day (day-after the latest
// blocker). Tooltip on each cell surfaces the ISO date + blocker plan info.
//
// Props:
//   startISO   — the picked freeze-window start (also anchors the strip)
//   plans      — ALL active plans for this machine (already filtered upstream)
//   days       — optional strip length in days (default 30)

const DAY_MS = 86400000;
const iso = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (isoStr, n) => iso(new Date(isoStr).getTime() + n * DAY_MS);

export default function MachineTimelinePeek({ startISO, plans = [], days = 30 }) {
  const { cells, freeFromISO } = useMemo(() => {
    if (!startISO) return { cells: [], freeFromISO: null };
    const startMs = new Date(startISO).getTime();
    const out = [];
    let latestBusy = null;
    for (let i = 0; i < days; i += 1) {
      const dayISO = iso(startMs + i * DAY_MS);
      const hit = plans.find(
        (p) => (p.planned_start || "") <= dayISO && (p.planned_finish || "") >= dayISO
      );
      if (hit && (!latestBusy || (hit.planned_finish || "") > latestBusy)) {
        latestBusy = hit.planned_finish;
      }
      out.push({ dayISO, plan: hit || null });
    }
    return {
      cells: out,
      freeFromISO: latestBusy ? addDays(latestBusy, 1) : null,
    };
  }, [startISO, plans, days]);

  if (!cells.length) return null;

  return (
    <div
      data-testid="planner-mach-timeline"
      className="mt-1 flex items-center gap-1.5"
      title={freeFromISO ? `Machine free from ${freeFromISO}` : "Machine free"}
    >
      <div className="flex gap-[1px]" data-testid="planner-mach-timeline-strip">
        {cells.map((c, idx) => {
          const isFreeFromDay = freeFromISO && c.dayISO === freeFromISO;
          const cls = c.plan
            ? "bg-orange-500/70 hover:bg-orange-400"
            : isFreeFromDay
            ? "bg-emerald-400 hover:bg-emerald-300"
            : "bg-zinc-700/60 hover:bg-zinc-600";
          const tip = c.plan
            ? `${c.dayISO} · busy · ${c.plan.item_code || c.plan.order_no || "plan"} (until ${c.plan.planned_finish})`
            : isFreeFromDay
            ? `${c.dayISO} · FREE FROM here`
            : `${c.dayISO} · free`;
          return (
            <span
              key={idx}
              title={tip}
              data-testid={`planner-mach-timeline-cell-${idx}`}
              className={`inline-block h-2.5 w-[4px] rounded-[1px] ${cls}`}
            />
          );
        })}
      </div>
      {freeFromISO && (
        <span
          data-testid="planner-mach-timeline-free-from"
          className="text-[9px] font-mono text-emerald-300 whitespace-nowrap"
        >
          → free {freeFromISO}
        </span>
      )}
    </div>
  );
}
